import { loadBassSound, renderBass, scheduleBassNote } from '../bass/bassVoice';
import React, { createContext, useContext, useState, useRef, useCallback, useEffect } from 'react';
import { API_URL } from '../utils/api';
import { sourceAudioDuration } from '../utils/sourceAudioDuration';
import { usePlaybackCoordinator } from './PlaybackCoordinatorContext';
import { 
  parseSessionKey, getScaleNotes, midiToFrequency, pickWeightedNote,
  generatePattern, detectGenre, getGenreOverrides
} from '../utils/knockEngine';

const PreviewContext = createContext();

export const usePreview = () => {
  const context = useContext(PreviewContext);
  if (!context) {
    throw new Error('usePreview must be used within a PreviewProvider');
  }
  return context;
};

export const PreviewProvider = ({ children }) => {
  const { requestOwnership, releaseOwnership, registerStopFn } = usePlaybackCoordinator();
  
  // Preview panel state
  const [isOpen, setIsOpen] = useState(false);
  const [tracks, setTracks] = useState([]); // Max MAX_TRACKS (8) tracks
  const [isPlaying, setIsPlaying] = useState(false);
  const [barLength, setBarLength] = useState(4); // 4 or 8 bars
  const [sessionBpm, setSessionBpm] = useState(null);

  // Pre-migration packs may have no catalog length. Read the real file once per
  // selected source, retaining identity so a late answer cannot overwrite a swap.
  const durationReadsRef = useRef(new Set());
  useEffect(() => {
    tracks.forEach(track => {
      if (Number(track.bars) > 0 || Number(track.durationSeconds) > 0 || !track.previewUrl) return;
      const source = `${track.loopId}|${track.previewUrl}`;
      if (durationReadsRef.current.has(source)) return;
      durationReadsRef.current.add(source);
      sourceAudioDuration(track.previewUrl).then(durationSeconds => {
        setTracks(current => current.map(selected => selected.loopId === track.loopId
          && selected.previewUrl === track.previewUrl ? { ...selected, durationSeconds } : selected));
      }).catch(() => { durationReadsRef.current.delete(source); });
    });
  }, [tracks]);

  
  // Crate state (download queue)
  const [crate, setCrate] = useState([]);
  const [crateOpen, setCrateOpen] = useState(false);
  
  // Knock Engine state
  const [knockEngineActive, setKnockEngineActive] = useState(false);
  const [knockEngineLoading, setKnockEngineLoading] = useState(false);
  const [knockEngineData, setKnockEngineData] = useState(null); // Generated bassline data
  const [knockEngineFeel, setKnockEngineFeel] = useState('basic'); // basic, bouncy, sparse, busy
  const [knockEngineVariation, setKnockEngineVariation] = useState(0); // Increment for variations
  const [knockEngineSoundIndex, setKnockEngineSoundIndex] = useState(0); // Current sound type index
  const [knockEngineVolume, setKnockEngineVolume] = useState(0.8); // Bass volume (0-1)
  const [knockEngineMuted, setKnockEngineMuted] = useState(false); // Mute state
  const [knockEngineKickFollow, setKnockEngineKickFollow] = useState(true); // Follow kick timing
  const [knockEngineRenderedBlob, setKnockEngineRenderedBlob] = useState(null); // Rendered audio for export
  const [detectedKickPositions, setDetectedKickPositions] = useState([]); // Kick positions (0-1 in loop)
  const knockGainNodeRef = useRef(null); // Master gain for bass volume control
  const knockBufferRef = useRef(null); // Decoded audio buffer
  const knockSourceRef = useRef(null); // Current playing source (for monophonic kill)
  const knockSchedulerRef = useRef(null); // setTimeout ID for scheduler
  const knockNextNoteTimeRef = useRef(0); // When next note should play (AudioContext time)
  const knockCurrentNoteIndexRef = useRef(0); // Current position in pattern
  const knockLoopStartTimeRef = useRef(0); // When current loop started (AudioContext time)
  const drumBufferRef = useRef(null); // Decoded drum loop audio for kick detection
  
  // Lookahead scheduling constants (in seconds)
  const SCHEDULE_AHEAD_TIME = 0.1; // How far ahead to schedule notes
  const SCHEDULER_INTERVAL = 25; // How often to check for notes to schedule (ms)
  
  // Audio state
  const audioContextRef = useRef(null);
  const trackAudioRef = useRef({}); // Store HTML Audio elements for simplicity
  const masterStartTimeRef = useRef(0);
  const masterAudioStartTimeRef = useRef(0); // AudioContext.currentTime when playback starts (precise sync point)
  const loopIntervalRef = useRef(null);
  // Loops play through HTML audio elements, which start some 10 to 150 ms after
  // play() is called, while the bass runs on the AudioContext clock. These refs let
  // the bass follow where the loops really are instead of where they were asked to be.
  // Running estimate of the play() start delay in seconds. Starts from what this
  // browser measured last time (per-device convenience; storage may be unavailable),
  // else 20 ms: loops are preloaded before play, so the real delay is usually small,
  // and a high first guess made the very first downbeat late.
  const learnedLagRef = useRef((() => {
    try {
      const saved = parseFloat(localStorage.getItem('sp-preview-start-lag'));
      return Number.isFinite(saved) && saved >= 0 && saved < 0.5 ? saved : 0.02;
    } catch (_) { return 0.02; }
  })());
  const knockSyncTimerRef = useRef(null);

  // The first audible, playing loop: the timing reference the bass follows.
  const referenceAudio = useCallback(() => (
    Object.values(trackAudioRef.current).find(audio => audio && !audio.paused && audio.volume > 0) || null
  ), []);

  // Measure where the loops actually are and move the bass onto them. Also learns the
  // start delay so the next loop's downbeat is placed correctly before it plays.
  const syncKnockToLoops = useCallback(() => {
    const ctx = audioContextRef.current;
    const ref = referenceAudio();
    if (!ctx || !ref || !(ref.currentTime > 0.03)) return;
    const actualStart = ctx.currentTime - ref.currentTime; // AudioContext time of loop position 0
    const measuredLag = actualStart - masterAudioStartTimeRef.current;
    if (measuredLag > -0.05 && measuredLag < 0.5) {
      learnedLagRef.current = learnedLagRef.current * 0.6 + Math.max(0, measuredLag) * 0.4;
      try { localStorage.setItem('sp-preview-start-lag', learnedLagRef.current.toFixed(4)); } catch (_) { /* optional */ }
    }
    const shift = actualStart - knockLoopStartTimeRef.current;
    if (knockLoopStartTimeRef.current && Math.abs(shift) > 0.004 && Math.abs(shift) < 0.5) {
      knockNextNoteTimeRef.current += shift;
      knockLoopStartTimeRef.current = actualStart;
    }
  }, [referenceAudio]);

  const scheduleKnockSync = useCallback(() => {
    clearTimeout(knockSyncTimerRef.current);
    // Wait until the element is really playing so currentTime is meaningful.
    knockSyncTimerRef.current = setTimeout(syncKnockToLoops, 250);
  }, [syncKnockToLoops]);
  
  // Raised from 6 to 8 by Russell, 2026-10-07. The Beat Arranger handoff carries
  // every track in the URL; 8 tracks plus a Knock Engine pattern measured ~9 KB,
  // well inside Cloudflare's ~16 KB URL ceiling.
  const MAX_TRACKS = 8;
  
  // Calculate loop duration in seconds based on BPM and bar length
  const getLoopDuration = useCallback((bpm, bars) => {
    const beatsPerBar = 4;
    const totalBeats = bars * beatsPerBar;
    return (totalBeats / bpm) * 60;
  }, []);
  
  // Start all tracks synchronized using HTML Audio with manual sync
  const startPlayback = useCallback(() => {
    if (tracks.length === 0 || !sessionBpm) return;
    
    // Request ownership — stops card playback if active
    requestOwnership('preview');
    
    const loopDuration = getLoopDuration(sessionBpm, barLength);
    
    // Ensure AudioContext exists and is running BEFORE playback starts
    // This is the shared context the Knock Engine will use
    if (!audioContextRef.current) {
      audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
    }
    if (audioContextRef.current.state === 'suspended') {
      audioContextRef.current.resume();
    }
    
    // Not started yet: the bass holds until the loops have loaded and really start,
    // instead of anchoring to the previous session's start time.
    masterAudioStartTimeRef.current = 0;

    // Stop any existing playback
    Object.values(trackAudioRef.current).forEach(audio => {
      if (audio) {
        audio.pause();
        audio.currentTime = 0;
      }
    });
    
    if (loopIntervalRef.current) {
      clearInterval(loopIntervalRef.current);
    }
    
    // Create audio elements for each track
    tracks.forEach(track => {
      if (!trackAudioRef.current[track.id]) {
        const audio = new Audio(track.previewUrl);
        audio.loop = false; // We'll handle looping manually for sync
        audio.volume = track.muted ? 0 : track.volume;
        audio.preload = 'auto';
        trackAudioRef.current[track.id] = audio;
      }
    });
    
    // Wait for all audio to be ready, then start together
    const startAllTracks = () => {
      const now = Date.now();
      masterStartTimeRef.current = now;
      // Record AudioContext.currentTime at the EXACT moment tracks start —
      // this is the precise sync point the Knock Engine locks to
      masterAudioStartTimeRef.current = audioContextRef.current.currentTime;
      // Anchor the bass downbeat to where the loops will actually start (call time
      // plus the learned start delay); syncKnockToLoops then corrects it.
      knockCurrentNoteIndexRef.current = 0;
      knockNextNoteTimeRef.current = Math.max(
        masterAudioStartTimeRef.current + learnedLagRef.current,
        audioContextRef.current.currentTime + 0.005
      );
      knockLoopStartTimeRef.current = knockNextNoteTimeRef.current;
      
      // Use trackAudioRef.current to get ALL current audio elements
      Object.values(trackAudioRef.current).forEach(audio => {
        if (audio && audio.volume > 0) {
          audio.currentTime = 0;
          audio.play().catch(e => console.error('Play error:', e));
        }
      });
      scheduleKnockSync();
    };
    
    // Load all tracks first
    Promise.all(
      tracks.map(track => {
        return new Promise((resolve) => {
          const audio = trackAudioRef.current[track.id];
          if (audio) {
            if (audio.readyState >= 3) {
              resolve();
            } else {
              audio.addEventListener('canplaythrough', () => resolve(), { once: true });
              audio.load();
            }
          } else {
            resolve();
          }
        });
      })
    ).then(() => {
      startAllTracks();
      
      // Set up loop interval to restart all tracks together
      // Use trackAudioRef.current (not tracks) so new tracks added during playback are included
      loopIntervalRef.current = setInterval(() => {
        const elapsed = (Date.now() - masterStartTimeRef.current) / 1000;
        // Restart when the loops have really reached the end, read from a playing
        // element. The wall clock started at play() and runs ahead of the audio by the
        // start delay, so restarting on it cut the end of every loop. Falls back to the
        // wall clock if no loop is audible or the reference file ended early.
        const ref = referenceAudio();
        const reachedEnd = ref && !ref.ended
          ? ref.currentTime >= loopDuration - 0.012
          : elapsed >= loopDuration - 0.012;
        
        if (reachedEnd) {
          // Restart ALL audio elements in trackAudioRef (includes newly added tracks)
          masterStartTimeRef.current = Date.now();
          // Hard resync: record actual AudioContext time at restart for Knock Engine
          masterAudioStartTimeRef.current = audioContextRef.current.currentTime;
          
          // HARD RESYNC Knock Engine — stop and restart from step 0 at new ground truth
          // This eliminates compounding float drift on every loop cycle.
          if (knockSchedulerRef.current) {
            clearTimeout(knockSchedulerRef.current);
            knockSchedulerRef.current = null;
          }
          knockCurrentNoteIndexRef.current = 0;
          // Place the downbeat where the loops will actually start (call time plus the
          // learned delay). The old fixed +50 ms push made every downbeat late.
          knockNextNoteTimeRef.current = Math.max(
            masterAudioStartTimeRef.current + learnedLagRef.current,
            audioContextRef.current.currentTime + 0.005
          );
          knockLoopStartTimeRef.current = knockNextNoteTimeRef.current;
          
          Object.values(trackAudioRef.current).forEach(audio => {
            if (audio) {
              audio.currentTime = 0;
              // Only play if not muted (volume > 0)
              if (audio.volume > 0) {
                audio.play().catch(e => console.error('Loop play error:', e));
              }
            }
          });
          scheduleKnockSync();
        }
      }, 10);
    });
    
    setIsPlaying(true);
  }, [tracks, sessionBpm, barLength, getLoopDuration, requestOwnership, referenceAudio, scheduleKnockSync]);
  
  // Stop all playback
  const stopPlayback = useCallback(() => {
    Object.values(trackAudioRef.current).forEach(audio => {
      if (audio) {
        audio.pause();
        audio.currentTime = 0;
      }
    });
    
    if (loopIntervalRef.current) {
      clearInterval(loopIntervalRef.current);
      loopIntervalRef.current = null;
    }
    clearTimeout(knockSyncTimerRef.current);
    
    setIsPlaying(false);
    releaseOwnership('preview');
  }, [releaseOwnership]);
  
  // Register stop function with coordinator
  useEffect(() => {
    registerStopFn('preview', stopPlayback);
  }, [registerStopFn, stopPlayback]);

  
  // Toggle playback
  const togglePlayback = useCallback(() => {
    if (isPlaying) {
      stopPlayback();
    } else {
      startPlayback();
    }
  }, [isPlaying, startPlayback, stopPlayback]);
  
  // Add track to preview
  const addToPreview = useCallback((loop) => {
    if (tracks.length >= MAX_TRACKS) {
      return { success: false, message: `Maximum ${MAX_TRACKS} tracks allowed` };
    }
    
    // No tempo restriction. The library is a repository, not a session: students
    // pair any tempos here and stretch them in Beat Arranger, which is where
    // time-stretching lives.
    //
    // This used to reject anything that was not the session tempo or an exact
    // half or double of it. Since the session tempo is set by whichever loop a
    // student happens to add first, one early pick quietly locked most of the
    // library away, and the rejection arrived as a toast after the click rather
    // than as anything visible beforehand. The tempo is surfaced on the card
    // instead, so the student can see the difference and decide.
    
    // Set session BPM if first track
    if (tracks.length === 0) {
      setSessionBpm(loop.bpm);
    }
    
    const newTrack = {
      id: `track_${Date.now()}`,
      loopId: loop.loop_id,
      title: loop.title,
      bpm: loop.bpm,
      bars: loop.bars ?? null,
      durationSeconds: loop.duration_seconds ?? null,
      musicalKey: loop.key,  // Add musical key
      previewUrl: loop.preview_url,
      downloadUrl: loop.download_url,
      instrumentGroup: loop.instrument_group,
      energy: loop.energy || null,  // Energy level 1-5
      packCode: loop.pack_code || null,  // Themed pack code
      styleTags: loop.style_tags || [],  // For genre detection
      volume: 0.8,
      muted: false
    };
    
    setTracks(prev => [...prev, newTrack]);
    // Adding no longer opens the panel. It used to slide up over the library on
    // every Add, so browsing meant closing it again each time. The dock bar shows
    // the new loop instead; the panel opens only when someone wants to mix.
    
    // Transfer ownership to preview — stops card playback if active
    requestOwnership('preview');
    
    // If already playing, create and start the new track
    if (isPlaying) {
      const audio = new Audio(newTrack.previewUrl);
      audio.volume = newTrack.volume;
      audio.loop = false;
      trackAudioRef.current[newTrack.id] = audio;
      
      // Sync with current playback position
      const loopDuration = getLoopDuration(sessionBpm, barLength);
      const elapsed = (Date.now() - masterStartTimeRef.current) / 1000;
      const position = elapsed % loopDuration;
      
      audio.addEventListener('canplaythrough', () => {
        audio.currentTime = Math.min(position, audio.duration - 0.1);
        audio.play().catch(e => console.error('New track play error:', e));
      }, { once: true });
      audio.load();
    }
    
    return { success: true };
  }, [tracks, sessionBpm, isPlaying, barLength, getLoopDuration, requestOwnership]);
  
  // Remove track from preview
  const removeFromPreview = useCallback((trackId) => {
    // Stop and clean up the track
    const audio = trackAudioRef.current[trackId];
    if (audio) {
      audio.pause();
      delete trackAudioRef.current[trackId];
    }
    
    setTracks(prev => {
      const newTracks = prev.filter(t => t.id !== trackId);
      // Reset session BPM if no tracks left
      if (newTracks.length === 0) {
        setSessionBpm(null);
        stopPlayback();
      }
      return newTracks;
    });
  }, [stopPlayback]);
  
  // Replace track (swap while playing)
  const replaceTrack = useCallback((trackId, newLoop) => {
    // No tempo restriction here either, for the same reason as addToPreview:
    // swapping a track for one at a different tempo is a legitimate thing to
    // want, and the stretching happens in Beat Arranger.
    
    // Stop old track
    const oldAudio = trackAudioRef.current[trackId];
    if (oldAudio) {
      oldAudio.pause();
    }
    
    // Update track info
    setTracks(prev => prev.map(t => 
      t.id === trackId ? {
        ...t,
        loopId: newLoop.loop_id,
        title: newLoop.title,
        bpm: newLoop.bpm,
        bars: newLoop.bars ?? null,
        durationSeconds: newLoop.duration_seconds ?? null,
        previewUrl: newLoop.preview_url,
        downloadUrl: newLoop.download_url,
        instrumentGroup: newLoop.instrument_group
      } : t
    ));
    
    // If playing, create and start new track at current position
    if (isPlaying) {
      const audio = new Audio(newLoop.preview_url);
      audio.volume = oldAudio?.volume || 0.8;
      audio.loop = false;
      trackAudioRef.current[trackId] = audio;
      
      const loopDuration = getLoopDuration(sessionBpm, barLength);
      const elapsed = (Date.now() - masterStartTimeRef.current) / 1000;
      const position = elapsed % loopDuration;
      
      audio.addEventListener('canplaythrough', () => {
        audio.currentTime = Math.min(position, audio.duration - 0.1);
        audio.play().catch(e => console.error('Replace track play error:', e));
      }, { once: true });
      audio.load();
    }
    
    return { success: true };
  }, [sessionBpm, isPlaying, barLength, getLoopDuration]);
  
  // Set track volume
  const setTrackVolume = useCallback((trackId, volume) => {
    setTracks(prev => prev.map(t => 
      t.id === trackId ? { ...t, volume } : t
    ));
    
    const audio = trackAudioRef.current[trackId];
    if (audio) {
      audio.volume = volume;
    }
  }, []);
  
  // Toggle track mute
  const toggleTrackMute = useCallback((trackId) => {
    setTracks(prev => prev.map(t => {
      if (t.id === trackId) {
        const newMuted = !t.muted;
        const audio = trackAudioRef.current[trackId];
        
        if (audio) {
          if (newMuted) {
            audio.volume = 0;
          } else {
            audio.volume = t.volume;
            // If playing and unmuting, sync position
            if (isPlaying) {
              const loopDuration = getLoopDuration(sessionBpm, barLength);
              const elapsed = (Date.now() - masterStartTimeRef.current) / 1000;
              const position = elapsed % loopDuration;
              audio.currentTime = Math.min(position, audio.duration - 0.1);
              audio.play().catch(e => console.error('Unmute play error:', e));
            }
          }
        }
        
        return { ...t, muted: newMuted };
      }
      return t;
    }));
  }, [isPlaying, sessionBpm, barLength, getLoopDuration]);
  
  // Check if loop is in preview
  const isInPreview = useCallback((loopId) => {
    return tracks.some(t => t.loopId === loopId);
  }, [tracks]);
  
  // Check BPM compatibility (exact match or single/double time)
  const isBpmCompatible = useCallback((bpm) => {
    if (!sessionBpm) return true;
    // Exact match
    if (bpm === sessionBpm) return true;
    // Double-time (e.g., 70 → 140)
    if (bpm === sessionBpm * 2) return true;
    // Half-time (e.g., 140 → 70)
    if (bpm * 2 === sessionBpm) return true;
    return false;
  }, [sessionBpm]);
  
  // Crate functions
  const addToCrate = useCallback((loop) => {
    if (crate.some(item => item.loop_id === loop.loop_id)) {
      return { success: false, message: 'Already in crate' };
    }
    setCrate(prev => [...prev, loop]);
    return { success: true };
  }, [crate]);
  
  // Helper function to write string to DataView (for WAV export)
  const writeString = (view, offset, string) => {
    for (let i = 0; i < string.length; i++) {
      view.setUint8(offset + i, string.charCodeAt(i));
    }
  };
  
  // Helper function to convert AudioBuffer to WAV blob
  const audioBufferToWav = useCallback((buffer) => {
    const numChannels = buffer.numberOfChannels;
    const sampleRate = buffer.sampleRate;
    const format = 1; // PCM
    const bitDepth = 16;
    
    const bytesPerSample = bitDepth / 8;
    const blockAlign = numChannels * bytesPerSample;
    
    const samples = buffer.length;
    const dataSize = samples * blockAlign;
    const headerSize = 44;
    const totalSize = headerSize + dataSize;
    
    const arrayBuffer = new ArrayBuffer(totalSize);
    const view = new DataView(arrayBuffer);
    
    // RIFF header
    writeString(view, 0, 'RIFF');
    view.setUint32(4, totalSize - 8, true);
    writeString(view, 8, 'WAVE');
    
    // fmt chunk
    writeString(view, 12, 'fmt ');
    view.setUint32(16, 16, true); // fmt chunk size
    view.setUint16(20, format, true);
    view.setUint16(22, numChannels, true);
    view.setUint32(24, sampleRate, true);
    view.setUint32(28, sampleRate * blockAlign, true);
    view.setUint16(32, blockAlign, true);
    view.setUint16(34, bitDepth, true);
    
    // data chunk
    writeString(view, 36, 'data');
    view.setUint32(40, dataSize, true);
    
    // Write interleaved samples
    let offset = 44;
    for (let i = 0; i < samples; i++) {
      for (let ch = 0; ch < numChannels; ch++) {
        const sample = Math.max(-1, Math.min(1, buffer.getChannelData(ch)[i]));
        const intSample = sample < 0 ? sample * 0x8000 : sample * 0x7FFF;
        view.setInt16(offset, intSample, true);
        offset += 2;
      }
    }
    
    return new Blob([arrayBuffer], { type: 'audio/wav' });
  }, []);
  
  // Render the generated bassline to an audio blob for export (defined early for addKnockEngineToCrate)
  const renderKnockEngineToBlob = useCallback(async () => {
    if (!knockEngineData) {
      return null;
    }
    
    const { pattern, bars } = knockEngineData;
    if (!pattern || pattern.length === 0) return null;
    
    const renderedBuffer = await renderBass({ pattern, bars: bars || 4, bpm: sessionBpm, volume: knockEngineVolume,
      sampleId: knockEngineData.sample_id, oscType: knockEngineData.osc_type, filterFreq: knockEngineData.filter_freq, detune: knockEngineData.detune });

    // Convert to WAV blob
    const wavBlob = audioBufferToWav(renderedBuffer);
    setKnockEngineRenderedBlob(wavBlob);
    return wavBlob;
  }, [knockEngineData, sessionBpm, knockEngineVolume, audioBufferToWav]);
  
  // Add generated Knock Engine bass to crate (with rendered audio)
  const addKnockEngineToCrate = useCallback(async () => {
    if (!knockEngineData) {
      return { success: false, message: 'No Knock Engine bass generated' };
    }
    
    // Check if already in crate
    if (crate.some(item => item.isKnockEngine && item.knockEngineData?.variation_seed === knockEngineData.variation_seed && item.knockEngineData?.feel === knockEngineData.feel)) {
      return { success: false, message: 'This bass is already in crate' };
    }
    
    // Render the bassline to a blob
    let renderedUrl = null;
    try {
      const blob = await renderKnockEngineToBlob();
      if (blob) {
        renderedUrl = URL.createObjectURL(blob);
      }
    } catch (e) {
      console.error('Failed to render knock engine:', e);
    }
    
    if (!renderedUrl) {
      return { success: false, message: 'Failed to render bassline' };
    }
    
    // Create a crate item for the generated bass
    const knockCrateItem = {
      loop_id: `knock_${knockEngineData.feel}_${Date.now()}`,
      title: `Knock Bass (${knockEngineData.template_name})`,
      previewUrl: renderedUrl,
      download_url: renderedUrl,
      bpm: knockEngineData.bpm,
      key: knockEngineData.root_note,
      instrumentGroup: 'Generated Bass',
      isKnockEngine: true,
      knockEngineData: {
        ...knockEngineData,
        volume: knockEngineVolume
      }
    };
    
    setCrate(prev => [...prev, knockCrateItem]);
    return { success: true };
  }, [knockEngineData, knockEngineVolume, crate, renderKnockEngineToBlob]);
  
  const removeFromCrate = useCallback((loopId) => {
    setCrate(prev => prev.filter(item => item.loop_id !== loopId));
  }, []);
  
  const isInCrate = useCallback((loopId) => {
    return crate.some(item => item.loop_id === loopId);
  }, [crate]);
  
  const clearCrate = useCallback(() => {
    setCrate([]);
  }, []);
  
  // Clear preview
  // Returns what was cleared so the caller can offer an undo. Clearing is one tap
  // on the dock bar, next to Send, so an accidental clear must be recoverable.
  const clearPreview = useCallback(() => {
    const snapshot = { tracks, sessionBpm, knockEngineActive, knockEngineData };
    stopPlayback();
    trackAudioRef.current = {};
    setTracks([]);
    setSessionBpm(null);
    // Also clear Knock Engine
    setKnockEngineActive(false);
    setKnockEngineData(null);
    return snapshot;
  }, [stopPlayback, tracks, sessionBpm, knockEngineActive, knockEngineData]);

  // Undo for clearPreview. Restores paused: audio players are rebuilt lazily by
  // startPlayback on the next play. Loops added after the clear are kept, after the
  // restored ones, up to the track limit.
  const restorePreview = useCallback((snapshot) => {
    if (!snapshot?.tracks?.length) return;
    stopPlayback();
    trackAudioRef.current = {};
    setTracks(prev => [
      ...snapshot.tracks,
      ...prev.filter(track => !snapshot.tracks.some(saved => saved.loopId === track.loopId)),
    ].slice(0, MAX_TRACKS));
    setSessionBpm(snapshot.sessionBpm);
    setKnockEngineActive(snapshot.knockEngineActive);
    setKnockEngineData(snapshot.knockEngineData);
  }, [stopPlayback]);

  // Undo for removing one loop: puts it back in its old slot if there is room and
  // it was not re-added meanwhile. Playback stops so the stack restarts in sync.
  const restoreTrack = useCallback((track, index) => {
    if (!track) return;
    stopPlayback();
    setTracks(prev => {
      if (prev.some(t => t.loopId === track.loopId) || prev.length >= MAX_TRACKS) return prev;
      const next = [...prev];
      next.splice(Math.min(index, next.length), 0, track);
      return next;
    });
    setSessionBpm(current => current ?? track.bpm ?? null);
  }, [stopPlayback]);
  
  // ============ Knock Engine ============
  
  // Stop the Knock Engine scheduler and playback (defined early so other functions can use it)
  const stopKnockPlayback = useCallback(() => {
    // 1. Clear scheduler IMMEDIATELY - no more notes will be scheduled
    if (knockSchedulerRef.current) {
      clearTimeout(knockSchedulerRef.current);
      knockSchedulerRef.current = null;
    }
    
    // 2. IMMEDIATELY silence the master gain - this stops ALL audio instantly
    if (knockGainNodeRef.current && audioContextRef.current) {
      const audioContext = audioContextRef.current;
      // Cancel any scheduled gain changes and set to 0 NOW
      knockGainNodeRef.current.gain.cancelScheduledValues(audioContext.currentTime);
      knockGainNodeRef.current.gain.setValueAtTime(0, audioContext.currentTime);
    }
    
    // 3. Stop current playing source IMMEDIATELY (time = 0, not + 0.01)
    if (knockSourceRef.current) {
      try {
        knockSourceRef.current.stop(0); // Stop NOW, not in 10ms
        knockSourceRef.current.disconnect();
      } catch (e) {}
      knockSourceRef.current = null;
    }
    
    // 4. Reset all timing indices
    knockCurrentNoteIndexRef.current = 0;
    knockNextNoteTimeRef.current = 0;
    knockLoopStartTimeRef.current = 0;
  }, []);
  
  // Detect kick positions from drum loop using simple transient detection
  // Returns array of positions (0-1) in the loop where kicks occur
  const detectKickPositions = useCallback(async (drumTrack) => {
    if (!drumTrack?.previewUrl) return [];
    
    try {
      // Create audio context if needed
      let audioContext = audioContextRef.current;
      if (!audioContext) {
        audioContext = new (window.AudioContext || window.webkitAudioContext)();
        audioContextRef.current = audioContext;
      }
      
      // Fetch and decode drum audio
      const response = await fetch(drumTrack.previewUrl);
      const arrayBuffer = await response.arrayBuffer();
      const audioBuffer = await audioContext.decodeAudioData(arrayBuffer);
      drumBufferRef.current = audioBuffer;
      
      // Simple kick detection: look for low-frequency transients
      // Kicks are typically in the 60-150Hz range with sharp attacks
      const channelData = audioBuffer.getChannelData(0); // Mono or left channel
      const sampleRate = audioBuffer.sampleRate;
      const totalSamples = channelData.length;
      
      // Analysis parameters
      const windowSize = Math.floor(sampleRate * 0.01); // 10ms windows
      const hopSize = Math.floor(sampleRate * 0.005); // 5ms hop
      const kickThreshold = 0.3; // Amplitude threshold
      const minKickGap = Math.floor(sampleRate * 0.1); // Min 100ms between kicks
      
      // Detect transients
      const kickPositions = [];
      let lastKickSample = -minKickGap;
      
      for (let i = 0; i < totalSamples - windowSize; i += hopSize) {
        // Calculate RMS energy in window
        let sum = 0;
        for (let j = 0; j < windowSize; j++) {
          sum += channelData[i + j] * channelData[i + j];
        }
        const rms = Math.sqrt(sum / windowSize);
        
        // Look for sharp attack (compare to previous window)
        if (i > hopSize) {
          let prevSum = 0;
          for (let j = 0; j < windowSize; j++) {
            prevSum += channelData[i - hopSize + j] * channelData[i - hopSize + j];
          }
          const prevRms = Math.sqrt(prevSum / windowSize);
          
          // Kick detected if:
          // 1. Current RMS is above threshold
          // 2. There's a sharp increase from previous window
          // 3. Enough time since last kick
          if (rms > kickThreshold && rms > prevRms * 1.5 && (i - lastKickSample) > minKickGap) {
            const position = i / totalSamples; // 0-1 position in loop
            kickPositions.push(position);
            lastKickSample = i;
          }
        }
      }
      
      // If detection failed or found too few kicks, use default positions (1 and 3)
      if (kickPositions.length < 2) {
        return [0.0, 0.5]; // Default: beat 1 and beat 3
      }
      
      // Limit to reasonable number and quantize to 16th note grid
      const quantizedPositions = kickPositions.slice(0, 8).map(pos => {
        // Quantize to nearest 16th (0.0625 = 1/16)
        return Math.round(pos / 0.0625) * 0.0625;
      });
      
      // Remove duplicates
      return [...new Set(quantizedPositions)].sort((a, b) => a - b);
      
    } catch (error) {
      console.error('Kick detection failed:', error);
      return [0.0, 0.5]; // Fallback: beat 1 and beat 3
    }
  }, []);
  
  // Generate kick-aware pattern based on detected kick positions
  const generateKickAwarePattern = useCallback((kickPositions, feel, basePattern) => {
    if (!kickPositions || kickPositions.length === 0) {
      return basePattern; // Fall back to template
    }
    
    // Basic feel stays template-driven
    if (feel === 'basic') {
      return basePattern;
    }
    
    // Build kick-aware pattern for Bouncy/Sparse/Busy
    const kickAwarePattern = [];
    
    // Note generation weights: 70% root, 20% octave, 10% fifth
    const getWeightedInterval = () => {
      const r = Math.random();
      if (r < 0.70) return 0;      // Root
      if (r < 0.90) return -12;    // Octave down
      return 7;                     // Fifth
    };
    
    // Add notes based on kick positions and feel
    kickPositions.forEach((kickPos, index) => {
      // Strong note ON the kick
      kickAwarePattern.push({
        beat: kickPos,
        duration: feel === 'sparse' ? 0.25 : 0.15,
        velocity: 1.0,
        interval: index === 0 ? 0 : getWeightedInterval() // First kick always root
      });
      
      if (feel === 'bouncy') {
        // Add bounce note slightly AFTER kick (16th note = 0.0625)
        const bouncePos = kickPos + 0.0625;
        if (bouncePos < 1.0 && !kickPositions.includes(bouncePos)) {
          kickAwarePattern.push({
            beat: bouncePos,
            duration: 0.1,
            velocity: 0.75,
            interval: getWeightedInterval()
          });
        }
      }
      
      if (feel === 'busy') {
        // Add notes before AND after kick
        const prePos = kickPos > 0.0625 ? kickPos - 0.0625 : null;
        const postPos = kickPos + 0.0625 < 1.0 ? kickPos + 0.0625 : null;
        
        if (prePos !== null && !kickPositions.includes(prePos)) {
          kickAwarePattern.push({
            beat: prePos,
            duration: 0.08,
            velocity: 0.65,
            interval: 0 // Ghost notes on root
          });
        }
        if (postPos !== null && !kickPositions.includes(postPos)) {
          kickAwarePattern.push({
            beat: postPos,
            duration: 0.1,
            velocity: 0.8,
            interval: getWeightedInterval()
          });
        }
      }
      
      // Sparse: just the kick hits, nothing extra (already added above)
    });
    
    // Sort by beat position
    kickAwarePattern.sort((a, b) => a.beat - b.beat);
    
    // Limit pattern length for sparse/busy
    if (feel === 'sparse' && kickAwarePattern.length > 4) {
      // Keep only notes on main kick positions
      return kickAwarePattern.filter(n => kickPositions.includes(n.beat)).slice(0, 4);
    }
    
    return kickAwarePattern;
  }, []);
  
  // Analyze drum track when drums change
  useEffect(() => {
    const drumTrack = tracks.find(t => t.instrumentGroup === 'Drums');
    if (drumTrack && knockEngineKickFollow) {
      detectKickPositions(drumTrack).then(positions => {
        setDetectedKickPositions(positions);
      });
    } else {
      setDetectedKickPositions([]);
      drumBufferRef.current = null;
    }
  }, [tracks, knockEngineKickFollow, detectKickPositions]);

  // Check if Knock Engine is available (need melody + drums)
  const knockEngineAvailable = useCallback(() => {
    const hasMelody = tracks.some(t => t.instrumentGroup === 'Melodies');
    const hasDrums = tracks.some(t => t.instrumentGroup === 'Drums');
    return hasMelody && hasDrums && sessionBpm;
  }, [tracks, sessionBpm]);
  
  // Get current session key from melody tracks
  const getSessionKey = useCallback(() => {
    const melodyTrack = tracks.find(t => t.instrumentGroup === 'Melodies' && t.musicalKey);
    return melodyTrack?.musicalKey || 'C';
  }, [tracks]);
  
  // Get style/mood tags from active tracks
  const getSessionContext = useCallback(() => {
    // Collect all style and mood info from tracks
    const styleTags = new Set();
    const moodTags = new Set();
    
    tracks.forEach(track => {
      if (track.styleTags) track.styleTags.forEach(t => styleTags.add(t));
      if (track.moodTags) track.moodTags.forEach(t => moodTags.add(t));
    });
    
    return {
      bpm: sessionBpm,
      key: getSessionKey(),
      style_tags: Array.from(styleTags),
      mood_tags: Array.from(moodTags)
    };
  }, [tracks, sessionBpm, getSessionKey]);
  
  const bassSampleRef = useRef(null);
  const [bassLoadError, setBassLoadError] = useState('');
  useEffect(() => {
    let alive = true; bassSampleRef.current = null; setBassLoadError('');
    if (knockEngineData?.sample_id) {
      const ctx = audioContextRef.current || new (window.AudioContext || window.webkitAudioContext)();
      audioContextRef.current = ctx;
      loadBassSound(ctx, knockEngineData.sample_id).then(sound => { if (alive) bassSampleRef.current = sound; }).catch(e => { if (alive) setBassLoadError(e.message); });
    }
    return () => { alive = false; };
  }, [knockEngineData?.sample_id]);
  const updateKnockBass = useCallback(value => {
    stopKnockPlayback();
    const presets = { 'Sub Sine': ['sine',300,0], 'Sawtooth Grit': ['sawtooth',500,0], 'Warm Round': ['sine',200,8], 'Square Punch': ['square',400,0], 'Triangle Soft': ['triangle',600,0] };
    const [osc,filter,detune] = presets[value.soundType] || presets['Sub Sine'];
    setKnockEngineData(prev => ({ ...prev, pattern: value.pattern, asset_url: null, sample_id: value.sampleId || null, sound_name: value.soundType, osc_type: osc, filter_freq: filter, detune, lock_to_key: value.lockToKey }));
  }, [stopKnockPlayback]);

  // Generate bassline from Knock Engine — client-side, scale-aware
  const activateKnockEngine = useCallback(async (options = {}) => {
    if (!knockEngineAvailable()) return;
    
    const {
      feel = knockEngineFeel,
      variationSeed = knockEngineVariation,
    } = options;
    
    setKnockEngineLoading(true);
    try {
      // Read session key and parse into root + scale
      const sessionKey = getSessionKey();
      const { root, scaleType } = parseSessionKey(sessionKey);
      
      // Detect genre from loaded loop style tags
      const allStyleTags = [];
      tracks.forEach(track => {
        if (track.styleTags) allStyleTags.push(...track.styleTags);
      });
      const genre = detectGenre(allStyleTags);
      const genreOvr = getGenreOverrides(genre);
      
      // Generate 64-step pattern using musical intelligence
      const pattern = generatePattern(
        root, scaleType, feel, genre, variationSeed,
        knockEngineKickFollow ? detectedKickPositions : []
      );
      
      // Store generated data
      const engineData = {
        pattern,
        root_note: root,
        scale_type: scaleType,
        feel,
        genre: genre || 'default',
        bpm: sessionBpm,
        bars: 4,
        template_name: `${feel} • ${genre || 'universal'}`,
        osc_type: genreOvr?.oscType || 'sine',
        filter_freq: genreOvr?.filter || 300,
        swing: genreOvr?.swing || 0,
        detune: genreOvr?.detune || 0,
        variation_seed: variationSeed,
      };
      
      setKnockEngineData(engineData);
      setKnockEngineActive(true);
    } catch (error) {
      console.error('Knock Engine error:', error);
      throw error;
    } finally {
      setKnockEngineLoading(false);
    }
  }, [knockEngineAvailable, getSessionKey, tracks, sessionBpm, knockEngineFeel, knockEngineVariation, knockEngineKickFollow, detectedKickPositions]);
  
  // Sound type presets for "New Sound" cycling
  const KNOCK_SOUND_TYPES = [
    { name: 'Sub Sine', oscType: 'sine', filterFreq: 300, detune: 0 },
    { name: 'Sawtooth Grit', oscType: 'sawtooth', filterFreq: 500, detune: 0 },
    { name: 'Warm Round', oscType: 'sine', filterFreq: 200, detune: 8 },
    { name: 'Square Punch', oscType: 'square', filterFreq: 400, detune: 0 },
    { name: 'Triangle Soft', oscType: 'triangle', filterFreq: 600, detune: 0 },
  ];

  // Variation: hot-swap the pattern without stopping playback
  const regenerateKnockEngine = useCallback(async () => {
    if (!knockEngineActive || !knockEngineData) return;
    
    // New seed for different output
    const newVariation = knockEngineVariation + 1;
    setKnockEngineVariation(newVariation);
    
    // Re-run pattern generator with same settings but new seed
    const sessionKey = getSessionKey();
    const { root, scaleType } = parseSessionKey(sessionKey);
    const allStyleTags = [];
    tracks.forEach(track => {
      if (track.styleTags) allStyleTags.push(...track.styleTags);
    });
    const genre = detectGenre(allStyleTags);
    
    const newPattern = generatePattern(
      root, scaleType, knockEngineFeel, genre, newVariation,
      knockEngineKickFollow ? detectedKickPositions : []
    );
    
    // Hot-swap: update pattern in knockEngineData without stopping the scheduler
    // The next scheduler tick will pick up the new pattern automatically
    setKnockEngineData(prev => ({
      ...prev,
      pattern: newPattern,
      variation_seed: newVariation,
    }));
  }, [knockEngineActive, knockEngineData, knockEngineVariation, getSessionKey, tracks, knockEngineFeel, knockEngineKickFollow, detectedKickPositions]);

  // New Sound: cycle through oscillator presets without stopping playback
  const cycleKnockEngineSound = useCallback(() => {
    if (!knockEngineActive || !knockEngineData) return;
    
    const nextIndex = (knockEngineSoundIndex + 1) % KNOCK_SOUND_TYPES.length;
    setKnockEngineSoundIndex(nextIndex);
    
    const sound = KNOCK_SOUND_TYPES[nextIndex];
    
    // Update synthesis parameters — next scheduled note uses new sound
    setKnockEngineData(prev => ({
      ...prev,
      osc_type: sound.oscType,
      filter_freq: sound.filterFreq,
      detune: sound.detune,
      sound_name: sound.name,
    }));
  }, [knockEngineActive, knockEngineData, knockEngineSoundIndex]);
  
  // Change feel and regenerate
  const changeKnockEngineFeel = useCallback(async (newFeel) => {
    setKnockEngineFeel(newFeel);
    
    // If already active, regenerate with new feel
    if (knockEngineActive) {
      // Stop current bass using the proper cleanup function
      stopKnockPlayback();
      
      await activateKnockEngine({ feel: newFeel });
    }
  }, [knockEngineActive, activateKnockEngine, stopKnockPlayback]);
  
  // Remove Knock Engine
  const removeKnockEngine = useCallback(() => {
    setKnockEngineActive(false);
    setKnockEngineData(null);
    
    // Stop any scheduled knock notes using the proper cleanup function
    stopKnockPlayback();
  }, [stopKnockPlayback]);
  
  // Update Knock Engine volume in real-time
  const updateKnockEngineVolume = useCallback((volume) => {
    setKnockEngineVolume(volume);
    // Update gain node immediately if it exists
    if (knockGainNodeRef.current && !knockEngineMuted) {
      knockGainNodeRef.current.gain.value = volume;
    }
  }, [knockEngineMuted]);
  
  // Toggle Knock Engine mute
  const toggleKnockEngineMute = useCallback(() => {
    setKnockEngineMuted(prev => {
      const newMuted = !prev;
      if (knockGainNodeRef.current) {
        knockGainNodeRef.current.gain.value = newMuted ? 0 : knockEngineVolume;
      }
      return newMuted;
    });
  }, [knockEngineVolume]);
  
  // Synthesize a single bass note at precise AudioContext time (monophonic - kills previous)
  const scheduleKnockNote = useCallback((audioContext, noteData, startTime, durationSec) => {
    if (knockEngineMuted) return;
    
    // Kill previous source (monophonic behavior)
    if (knockSourceRef.current) {
      try {
        knockSourceRef.current.stop(startTime + 0.005);
      } catch (e) {}
    }
    
    // Get synthesis options from engine data
    const oscType = knockEngineData?.osc_type || 'sine';
    const filterFreq = knockEngineData?.filter_freq || 300;
    const detune = knockEngineData?.detune || 0;
    
    const pattern = knockEngineData?.pattern || [];
    const index = pattern.findIndex(n => n.step === noteData.step);
    const sample = bassSampleRef.current;
    if (knockEngineData?.sample_id && sample?.id !== knockEngineData.sample_id) return;
    const destination = knockGainNodeRef.current || audioContext.destination;
    const osc = scheduleBassNote(audioContext, destination, noteData, pattern[index - 1], startTime, durationSec,
      { oscType, filterFreq, detune, sample: knockEngineData?.sample_id ? sample : null }, 60 / sessionBpm / 4, pattern[index + 1]);

    knockSourceRef.current = osc;
  }, [knockEngineMuted, knockEngineData, sessionBpm]);
  
  // Get current position in the loop (0-1 range representing position in 4-bar cycle)
  const getLoopPosition = useCallback(() => {
    if (!masterStartTimeRef.current || !sessionBpm) return 0;
    
    const loopDurationMs = getLoopDuration(sessionBpm, barLength) * 1000;
    const elapsed = Date.now() - masterStartTimeRef.current;
    return (elapsed % loopDurationMs) / loopDurationMs;
  }, [sessionBpm, barLength, getLoopDuration]);
  
  // Lookahead scheduler — 64-step grid, locked to shared AudioContext clock
  const knockScheduler = useCallback(() => {
    if (!audioContextRef.current || !knockEngineData || !isPlaying || !knockEngineActive) {
      return;
    }
    
    const audioContext = audioContextRef.current;
    const currentTime = audioContext.currentTime;
    const { pattern, bars, swing } = knockEngineData;
    
    if (!pattern || pattern.length === 0) return;
    
    // Calculate step timing from session BPM
    const secondsPerBeat = 60.0 / sessionBpm;
    const secondsPerStep = secondsPerBeat / 4; // 16th-note grid
    const totalSteps = (bars || 4) * 16;       // 64 steps for 4 bars
    const loopDurationSec = totalSteps * secondsPerStep;
    
    // Schedule notes within the lookahead window
    while (knockNextNoteTimeRef.current < currentTime + SCHEDULE_AHEAD_TIME) {
      const currentStep = knockCurrentNoteIndexRef.current % totalSteps;
      
      // Find note at this step
      const noteData = pattern.find(n => n.step === currentStep);
      
      if (noteData) {
        // Apply swing offset for off-beat steps (every other 8th note)
        let swingOffset = 0;
        if (swing && currentStep % 2 === 1) {
          swingOffset = secondsPerStep * swing;
        }
        
        const noteStartTime = knockNextNoteTimeRef.current + swingOffset;
        const noteDuration = (noteData.durationSteps || 1) * secondsPerStep;
        
        // Only schedule if in the future
        if (noteStartTime >= currentTime) {
          scheduleKnockNote(audioContext, noteData, noteStartTime, noteDuration);
        }
      }
      
      // Advance one step
      knockNextNoteTimeRef.current += secondsPerStep;
      knockCurrentNoteIndexRef.current++;
      
      // Loop after full pattern cycle — DO NOT advance knockLoopStartTimeRef here.
      // The loop restart handler does a hard resync instead of incremental advance,
      // preventing compounding float error.
      if (knockCurrentNoteIndexRef.current % totalSteps === 0) {
        knockCurrentNoteIndexRef.current = 0;
      }
    }
  }, [knockEngineData, isPlaying, knockEngineActive, sessionBpm, scheduleKnockNote, SCHEDULE_AHEAD_TIME]);
  
  // Start the Knock Engine scheduler loop
  const startKnockScheduler = useCallback(() => {
    if (!audioContextRef.current) return;
    
    const audioContext = audioContextRef.current;
    
    // ALWAYS reset step counter to 0 on start
    knockCurrentNoteIndexRef.current = 0;
    
    // SYNC: anchor to where the loops actually start. While they are still loading
    // (master time 0), hold: startPlayback anchors the bass when they really begin.
    const now = audioContext.currentTime;
    const loopStart = masterAudioStartTimeRef.current + learnedLagRef.current;
    if (!masterAudioStartTimeRef.current) {
      knockLoopStartTimeRef.current = 0;
      knockNextNoteTimeRef.current = Number.POSITIVE_INFINITY;
    } else if (loopStart >= now) {
      knockLoopStartTimeRef.current = loopStart;
      knockNextNoteTimeRef.current = loopStart;
    } else {
      // Turned on while the loops are already playing: join on the right step of the
      // pattern instead of starting from step 0 wherever the loops happen to be.
      const secondsPerStep = 60 / (sessionBpm || 90) / 4;
      const totalSteps = ((knockEngineData && knockEngineData.bars) || 4) * 16;
      const stepsElapsed = Math.ceil((now + 0.005 - loopStart) / secondsPerStep);
      knockLoopStartTimeRef.current = loopStart;
      knockCurrentNoteIndexRef.current = stepsElapsed % totalSteps;
      knockNextNoteTimeRef.current = loopStart + stepsElapsed * secondsPerStep;
    }
    // Then correct against where the loops really are.
    scheduleKnockSync();
    
    // Restore gain node to proper level (was set to 0 on stop)
    if (knockGainNodeRef.current) {
      knockGainNodeRef.current.gain.cancelScheduledValues(audioContext.currentTime);
      knockGainNodeRef.current.gain.setValueAtTime(
        knockEngineMuted ? 0 : knockEngineVolume, 
        audioContext.currentTime
      );
    }
    
    // Start scheduler loop
    const runScheduler = () => {
      knockScheduler();
      knockSchedulerRef.current = setTimeout(runScheduler, SCHEDULER_INTERVAL);
    };
    
    runScheduler();
  }, [knockScheduler, SCHEDULER_INTERVAL, knockEngineMuted, knockEngineVolume, sessionBpm, knockEngineData, scheduleKnockSync]);
  
  // Load the bass sample into buffer when data changes
  useEffect(() => {
    if (!knockEngineData?.asset_url) {
      knockBufferRef.current = null;
      return;
    }
    
    // Create audio context if needed
    let audioContext = audioContextRef.current;
    if (!audioContext) {
      audioContext = new (window.AudioContext || window.webkitAudioContext)();
      audioContextRef.current = audioContext;
    }
    
    // Create master gain node if needed
    if (!knockGainNodeRef.current) {
      knockGainNodeRef.current = audioContext.createGain();
      knockGainNodeRef.current.connect(audioContext.destination);
    }
    knockGainNodeRef.current.gain.value = knockEngineMuted ? 0 : knockEngineVolume;
    
    // Load and decode the sample — read response body exactly once
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(knockEngineData.asset_url);
        const arrayBuffer = await res.arrayBuffer(); // consume body ONCE
        if (cancelled) return;
        const audioBuffer = await audioContext.decodeAudioData(arrayBuffer);
        if (cancelled) return;
        knockBufferRef.current = audioBuffer;
      } catch (err) {
        if (!cancelled) console.error('Failed to load bass sample:', err);
      }
    })();
    
    return () => { cancelled = true; };
  }, [knockEngineData?.asset_url, knockEngineMuted, knockEngineVolume]);
  
  // Sync Knock Engine to main transport - start/stop with playback
  useEffect(() => {
    if (!isPlaying || !knockEngineActive || !knockEngineData) {
      // Stop playback immediately when conditions not met
      stopKnockPlayback();
      return;
    }
    
    // Resume audio context if suspended
    if (audioContextRef.current?.state === 'suspended') {
      audioContextRef.current.resume();
    }
    
    // Start the lookahead scheduler
    startKnockScheduler();
    
    return () => {
      stopKnockPlayback();
    };
  }, [isPlaying, knockEngineActive, knockEngineData, startKnockScheduler, stopKnockPlayback]);
  
  // Toggle Kick Follow mode
  const toggleKnockEngineKickFollow = useCallback(() => {
    setKnockEngineKickFollow(prev => !prev);
  }, []);
  
  // Cleanup on unmount
  useEffect(() => {
    return () => {
      stopPlayback();
      Object.values(trackAudioRef.current).forEach(audio => {
        if (audio) {
          audio.pause();
          audio.src = '';
        }
      });
    };
  }, [stopPlayback]);
  
  // Restart playback when bar length changes
  useEffect(() => {
    if (isPlaying && tracks.length > 0) {
      stopPlayback();
      // Small delay then restart
      setTimeout(() => {
        startPlayback();
      }, 100);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [barLength]);
  
  const value = {
    // Preview panel
    isOpen,
    setIsOpen,
    tracks,
    isPlaying,
    barLength,
    setBarLength,
    sessionBpm,
    togglePlayback,
    addToPreview,
    removeFromPreview,
    replaceTrack,
    setTrackVolume,
    toggleTrackMute,
    isInPreview,
    isBpmCompatible,
    clearPreview,
    restorePreview,
    restoreTrack,
    MAX_TRACKS,
    
    // Crate
    crate,
    crateOpen,
    setCrateOpen,
    addToCrate,
    addKnockEngineToCrate,
    removeFromCrate,
    isInCrate,
    clearCrate,
    
    // Knock Engine
    knockEngineActive,
    knockEngineLoading,
    knockEngineData,
    knockEngineFeel,
    knockEngineVolume,
    knockEngineMuted,
    knockEngineKickFollow,
    knockEngineRenderedBlob,
    detectedKickPositions,
    knockEngineAvailable,
    activateKnockEngine,
    regenerateKnockEngine,
    cycleKnockEngineSound,
    changeKnockEngineFeel,
    updateKnockEngineVolume,
    toggleKnockEngineMute,
    toggleKnockEngineKickFollow,
    renderKnockEngineToBlob, updateKnockBass, bassLoadError,
    removeKnockEngine
  };
  
  return (
    <PreviewContext.Provider value={value}>
      {children}
    </PreviewContext.Provider>
  );
};

export default PreviewContext;
